import { guards } from '@shared/helpers/typeguards.helpers';

/**
 * Design map "Show" checkboxes (background, nearby buildings, blueprint).
 * Persisted in localStorage so a PDF import's tracing layout is remembered.
 */
export type DesignMapLayerSettings = {
  showBackgroundMap: boolean;
  showNearbyBuildings: boolean;
  showBlueprintUnderlay: boolean;
};

/** Defaults before the user has changed anything (basemap on, no PDF overlay pref). */
export const DEFAULT_DESIGN_MAP_LAYER_SETTINGS: DesignMapLayerSettings = {
  showBackgroundMap: true,
  showNearbyBuildings: false,
  showBlueprintUnderlay: true,
};

/**
 * Checkbox state after placing a PDF on the map: blueprint on, basemap and
 * nearby 3D buildings off so the sheet is readable.
 */
export const PDF_IMPORT_MAP_LAYER_SETTINGS: DesignMapLayerSettings = {
  showBackgroundMap: false,
  showNearbyBuildings: false,
  showBlueprintUnderlay: true,
};

export const DESIGN_MAP_LAYER_SETTINGS_STORAGE_KEY =
  'nodon.design-map.layer-settings';

const booleanOrDefault = (value: unknown, fallback: boolean): boolean =>
  guards.is.boolean(value) ? value : fallback;

/**
 * Merge persisted Show-checkbox JSON with defaults so a partial or corrupt
 * localStorage value cannot leave the map in an unrecoverable layer state.
 */
export const parseDesignMapLayerSettings = (
  value: unknown,
): DesignMapLayerSettings => {
  if (!guards.is.object(value)) return DEFAULT_DESIGN_MAP_LAYER_SETTINGS;
  return {
    showBackgroundMap: booleanOrDefault(
      value.showBackgroundMap,
      DEFAULT_DESIGN_MAP_LAYER_SETTINGS.showBackgroundMap,
    ),
    showNearbyBuildings: booleanOrDefault(
      value.showNearbyBuildings,
      DEFAULT_DESIGN_MAP_LAYER_SETTINGS.showNearbyBuildings,
    ),
    showBlueprintUnderlay: booleanOrDefault(
      value.showBlueprintUnderlay,
      DEFAULT_DESIGN_MAP_LAYER_SETTINGS.showBlueprintUnderlay,
    ),
  };
};
